// src/modules/operations/dashboard/widgets/shells/HoursTrendChart.tsx
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Clock } from "lucide-react";
import { apiFetch } from "@/modules/shared/lib/api-client";
import { linearForecast } from "@/modules/shared/dashboard/forecast";

type Trend = { points: { month: string; total: number }[] };

export function HoursTrendChart() {
  const { data } = useQuery<Trend>({
    queryKey: ["widget", "/operations/dashboard/hours-trend"],
    queryFn: () => apiFetch<Trend>("/operations/dashboard/hours-trend"),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const allPoints = linearForecast(data?.points ?? [], 3);
  const actualPoints = allPoints.filter((p) => !p.projected);
  const forecastPoints = allPoints.filter((p, i, arr) => {
    if (p.projected) return true;
    const nextIsProjected = arr[i + 1]?.projected;
    return !!nextIsProjected;
  });

  return (
    <div className="ol-card flat col-span-2 flex flex-col gap-3">
      <div className="ol-card-head">
        <div className="ol-card-ic ink"><Clock /></div>
        <div className="ol-card-title">
          <div className="ol-card-name"><span className="n">Horas 6 meses</span></div>
          {allPoints.some((p) => p.projected) && (
            <span className="ol-kpi-l" style={{ fontSize: 11 }}>+ projeção 3 meses</span>
          )}
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={allPoints}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fill: "#6b716b", fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fill: "#6b716b", fontSize: 11 }} />
            <Tooltip />
            {/* Actual line */}
            <Line
              data={actualPoints}
              type="monotone"
              dataKey="total"
              stroke="#0b0d0b"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "var(--lime)", stroke: "#0b0d0b", strokeWidth: 1.5 }}
              isAnimationActive={false}
            />
            {/* Forecast line (dashed) */}
            {forecastPoints.length > 1 && (
              <Line
                data={forecastPoints}
                type="monotone"
                dataKey="total"
                stroke="#0b0d0b"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  if (!payload?.projected) {
                    return <circle key={`hrs-dot-${cx}`} cx={cx} cy={cy} r={4} fill="var(--lime)" stroke="#0b0d0b" strokeWidth={1.5} />;
                  }
                  return <circle key={`hrs-dot-${cx}`} cx={cx} cy={cy} r={3} fill="#fff" stroke="#0b0d0b" strokeWidth={1.5} strokeDasharray="2 2" />;
                }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
